import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { PxToVw } from '@/utils/convertPx'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { SectionTitleBackground } from '@/components/elements/SectionTitleBackground'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { CompanyLinks } from '@/components/layouts/Company/CompanyLinks'
import { Head } from '@/components/layouts/Head'

export const HistoryPage = () => {
  return (
    <>
      <Head
        title="会社沿革"
        description="トライアルホールディングスのこれまでの歩みをご紹介します。"
        ogp={{ type: 'article', url: 'company/history/' }}
      />
      <HistoryMainWrapper>
        <Spacer h={{ pc: 69, sp: 40 }} />
        <HistoryMain>
          <SectionTitle
            titleText="History"
            subTitleText="会社沿革"
            color="normal"
          />
          <NoteDiv>
            <Note>
              ITを祖業としながら、スーパーセンター業態の確立や居抜出店によるGMS（総合スーパー）再生、Retail
              AIによるSmart Store Technology ®️
              の開発や他企業との共創など、あらゆることに挑戦してきました。
              <br />
              1999年から20年以上、売上高は増加し続けています。
              挑戦の歴史をご紹介します。
            </Note>
          </NoteDiv>
          <Spacer h={{ pc: 90, sp: 0 }} />
          <HistoryFlow>
            <HistoryFlowWrapper>
              <SectionTitleBackground
                bgColor="white"
                outerBgColor="thin-blue"
                showTopArrow={false}
                padding={{
                  pc: `0 0 ${PxToVw(100, 1440)}`,
                  sp: `80px 0 40px`,
                }}
              >
                <PeriodTitle id="1970">
                  <p>流通小売×ITの始まり</p>
                  <h3>
                    1970-1980<span>年</span>
                  </h3>
                </PeriodTitle>
              </SectionTitleBackground>
              <HistoryFlowInner>
                <Spacer h={{ pc: 0, sp: 40 }} />
                <PeriodListWrapper>
                  <PeriodList>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        1974<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>あさひ屋 開業</h5>
                          福岡市に古物商、リサイクルショップとして開業。
                          <br />
                          トライアルの前身。
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img
                          src="/images/asahiya.webp"
                          alt="株式会社あさひや"
                        />
                      </PeriodImageWrapper>
                    </PeriodItem>
                    <PeriodItem circlePosition="left" top={-200}>
                      <h4>
                        1984<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>㈱トライアルカンパニー誕生</h5>
                          あさひ屋を㈱トライアルカンパニーに商号変更。
                          <br />
                          小売店向けのPOS（販売時点情報管理）システム開発や、大手コンピューターメーカーの受託を行いました。
                          <br />
                          「自分たちでやる」というスピリットで、開発を内製しました。
                        </li>
                        <li>
                          <h5>エレビット開店</h5>
                          AV機器などを販売する家電量販店をスタート。（現在は閉店）
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img src="/images/elebit.webp" alt="エレビット住吉店" />
                      </PeriodImageWrapper>
                    </PeriodItem>
                    <PeriodItem circlePosition="right" top={-200}>
                      <h4>
                        1992<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>トライアル1号店開店</h5>
                          福岡県大野城市に、ディスカウントストア
                          トライアルがオープン。（現在は閉店）
                        </li>
                      </ul>
                    </PeriodItem>
                    <PeriodItem circlePosition="left" top={141}>
                      <h4>
                        1996<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>スーパーセンター1号店開店</h5>
                          当時アメリカで盛況であったスーパーセンター業態に着想を得て、福岡県北九州市にオープン。
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img
                          src="/images/kitakyusyukuukou.webp"
                          alt="スーパーセンタートライアル北九州空港バイパス店オープン"
                        />
                      </PeriodImageWrapper>
                    </PeriodItem>
                  </PeriodList>
                </PeriodListWrapper>
                <Spacer h={{ pc: 30, sp: 0 }} />
              </HistoryFlowInner>
            </HistoryFlowWrapper>
            <HistoryFlowWrapper>
              <SectionTitleBackground
                bgColor="white"
                outerBgColor="thin-blue"
                padding={{
                  pc: `max(175px, ${PxToVw(210, 1440)}) 0 max(120px, ${PxToVw(100, 1440)})`,
                  sp: '80px 0 40px',
                }}
              >
                <PeriodTitle id="2001">
                  <p>GMS居抜による大量出店とたゆまぬIT開発</p>
                  <h3>
                    2001-2010<span>年</span>
                  </h3>
                </PeriodTitle>
              </SectionTitleBackground>
              <HistoryFlowInner>
                <Spacer h={{ pc: 0, sp: 40 }} />
                <PeriodListWrapper>
                  <PeriodList>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        2001<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>GMS撤退後の物件に居抜出店を開始</h5>
                          高度経済成長期に栄えた総合スーパー（GMS）業態。消費者のニーズや時代の変化と共に、厳しい経営状況を理由に、閉店していく店舗が多くなりました。
                          <br />
                          GMS他店撤退後に空いた物件に、トライアルが入居する形で出店。商売を熟知した専門社員と一緒に業態を確立しながら、日本全国に出店を加速していきました。
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img src="/images/utsunomiya.webp" alt="宇都宮店" />
                      </PeriodImageWrapper>
                    </PeriodItem>
                    <PeriodItem circlePosition="left" top={-200}>
                      <h4>
                        2003<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>中国にソフトウェア開発拠点を設立</h5>
                          中国煙台市にソフトウェア開発拠点を設けて、現地エンジニアの採用など、ITシステムやデバイスの開発強化を行いました。
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img src="/images/vantaiTRE.webp" alt="中国煙台市TRE" />
                      </PeriodImageWrapper>
                    </PeriodItem>
                    <PeriodItem circlePosition="right" top={-100}>
                      <h4>
                        2009<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>㈱カウボーイを完全子会社化</h5>
                          北海道に8店舗を展開していた、精肉を強みとするディスカウント食品スーパー「カウボーイ」を完全子会社化して、トライアルに業態転換。
                        </li>
                      </ul>
                    </PeriodItem>
                  </PeriodList>
                </PeriodListWrapper>
                <Spacer h={{ pc: 30, sp: 0 }} />
              </HistoryFlowInner>
            </HistoryFlowWrapper>
            <HistoryFlowWrapper>
              <SectionTitleBackground
                bgColor="white"
                outerBgColor="thin-blue"
                padding={{
                  pc: `max(175px, ${PxToVw(210, 1440)}) 0 max(120px, ${PxToVw(100, 1440)})`,
                  sp: '80px 0 40px',
                }}
              >
                <PeriodTitle id="2010">
                  <p>
                    外部企業と一緒に価値共創、Smart Store Technology開発を加速
                  </p>
                  <h3>
                    2010<span>年</span>-
                  </h3>
                </PeriodTitle>
              </SectionTitleBackground>
              <HistoryFlowInner>
                <Spacer h={{ pc: 0, sp: 40 }} />
                <PeriodListWrapper>
                  <PeriodList>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        2013<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>データプラットフォーム「MD-Link」稼働開始</h5>
                          商品の製造メーカーと、店舗に係るあらゆるデータを共有することができるMD-Linkを開発運用開始。データを共有しながら全体最適を図るための、オープンイノベーションを実施。
                        </li>
                      </ul>
                    </PeriodItem>
                    <PeriodItem circlePosition="left" top={0}>
                      <h4>
                        2015<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>㈱トライアルホールディングス誕生</h5>
                          持株会社体制へ移行
                        </li>
                      </ul>
                    </PeriodItem>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        2016<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>Joint Business Plan（JBP）の取り組み開始</h5>
                          メーカーや卸売企業など、サプライチェーンにおける関係各社と一緒になって、流通小売業界に存在するムダ・ムラ・ムリを解消するため取り組みを開始。
                          <br />
                          毎年JBPサミットを開催して、業界の課題解決に向けて経営トップが協議。
                        </li>
                      </ul>
                    </PeriodItem>
                    <PeriodItem circlePosition="left" top={0}>
                      <h4>
                        2018<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>㈱Retail AI設立</h5>
                          決済タブレット端末付きショッピングカート（Skip Cart）や在庫状況などを把握するAIカメラの開発など、流通小売業界に変革をもたらすソリューションを提供。
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img
                          src="/images/retailai-entrance.webp"
                          alt="Retailエントランス"
                        />
                      </PeriodImageWrapper>
                      <ul>
                        <li>
                          <h5>
                            スーパーセンタートライアル アイランドシティ店
                            <br />
                            オープン
                          </h5>
                          Skip CartやAIカメラ等のIoTデバイスを導入し、新しい買い物体験ができる日本初のスマートストアである「スーパーセンタートライアル アイランドシティ店」（福岡市東区）を開店
                        </li>
                      </ul>
                    </PeriodItem>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        2021<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          日本最大のリテールDX（小売のIT/AI化）の最先端基地を目指し、産官学のまちづくりとしてリモートワークタウン
                          ムスブ宮若プロジェクトが本格始動
                        </li>
                      </ul>
                    </PeriodItem>

                    <PeriodItem circlePosition="left" top={0}>
                      <h4>
                        2022<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          最先端のIT技術を導入した次世代型スマートストアとしてTRIAL
                          GO脇田店（福岡県宮若市）を開店
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img src="/images/TRIALGO_Wakita.jpeg" alt="TRIALGO脇田店" />
                      </PeriodImageWrapper>
                    </PeriodItem>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        2023<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          ㈱青森トライアル及び㈱トライアルカンパニーが㈱佐藤長及び㈱青森食研から一部事業を承継
                        </li>
                      </ul>
                    </PeriodItem>
                    {/* 3月21日に対応 */}
                    <PeriodItem circlePosition="left" top={0}>
                      <h4>
                        2024<span>年</span>
                      </h4>
                      <ul>
                        <li>東京証券取引所グロース市場上場</li>
                      </ul>
                    </PeriodItem>
                    <PeriodItem circlePosition="right" top={0}>
                      <h4>
                        2025<span>年</span>
                      </h4>
                      <ul>
                        <li>
                          <h5>関東を中心に店舗を展開する㈱西友を完全子会社化</h5>
                          食料品､衣料品､住居用品などを販売する小売チェーン「西友」を完全子会社化。245店舗がグループ入り。
                        </li>
                      </ul>
                      <Spacer h={{ pc: 20, sp: 22 }} />
                      <PeriodImageWrapper>
                        <img src="/images/Store_SEIYU.png" alt="西友店" />
                      </PeriodImageWrapper>
                    </PeriodItem>
                    <PeriodItem circlePosition="left" top={0}>
                      <h4>
                        2026<span>年</span>
                      </h4>
                      <ul>
                        <li>TOPIX（東証株価指数）構成銘柄への新規採用</li>
                      </ul>
                    </PeriodItem>

                  </PeriodList>
                </PeriodListWrapper>
              </HistoryFlowInner>
            </HistoryFlowWrapper>
          </HistoryFlow>
        </HistoryMain>
        <CardLinksDiv>
          <Spacer h={{ pc: 130, sp: 0 }} />
          <CompanyLinks padding={{ pc: '0px auto 0' }} />
          <Spacer h={{ pc: 130, sp: 0 }} />
        </CardLinksDiv>
      </HistoryMainWrapper>
    </>
  )
}

HistoryPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Corporate Information',
    subTitle: '企業情報',
  }

  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '企業情報', path: '/company/' },
    { name: '会社沿革', path: '' },
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs}>
      {page}
    </TitleLayout>
  )
}

const HistoryMainWrapper = styled.div`
  overflow: hidden;
`

const HistoryMain = styled.div``

const HistoryFlow = styled.div``

const HistoryFlowWrapper = styled.div`
  margin: 0 calc(50% - 50vw);
  width: 100vw;
  background-color: ${theme.colors.backgroundBlue};
  position: relative;

  &::after {
    content: '';
    width: 2px;
    height: 100%;
    background-color: #fff;
    position: absolute;
    left: 50%;
    top: 0;
  }

  @media screen and (max-width: 1047px) {
    padding-bottom: 50px;

    &::after {
      display: none;
    }

    &:last-child {
      padding-bottom: 80px;
    }
  }
`

const HistoryFlowInner = styled.div`
  width: 1040px;
  margin: 0 auto;

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 0 20px;
  }
`

const PeriodTitle = styled.div`
  position: relative;
  text-align: center;
  font-family: ${theme.fontEn};
  font-weight: bold;
  color: ${theme.colors.trialBlue};
  z-index: 9;

  h3 {
    font-size: 38px;
  }

  p {
    font-size: 18px;
    line-height: 34px;
    letter-spacing: 0.36px;
    color: ${theme.colors.textTitle};
  }

  span {
    font-size: 22px;
  }

  @media screen and (max-width: 1047px) {
    h3 {
      font-size: 32px;
    }

    span {
      font-size: 20px;
    }
  }
`

const PeriodListWrapper = styled.div``

const PeriodList = styled.div`
  width: 50%;

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const PeriodItem = styled.div<{
  circlePosition: 'left' | 'right'
  top: number
}>`
  margin-top: ${({ top }) => `${top}px`};
  position: relative;
  left: ${({ circlePosition }) => (circlePosition === 'left' ? '100%' : '')};

  h4 {
    font-family: ${theme.fontEn};
    font-size: 30px;
    color: ${theme.colors.trialBlue};
    border-bottom: 1px solid ${theme.colors.trialBlue};
    position: relative;
    padding-bottom: 16px;

    padding-left: ${({ circlePosition }) =>
      circlePosition === 'left' ? '30px' : ''};

    &::after {
      content: '';
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: ${theme.colors.trialBlue};
      display: inline-block;
      position: absolute;
      bottom: 0;
      transform: translate(50%, 50%);
      z-index: 9;

      ${({ circlePosition }) => `${circlePosition}: 0`};
      transform: ${({ circlePosition }) =>
        circlePosition === 'right'
          ? 'translate(50%, 50%)'
          : 'translate(-50%, 50%)'};
    }
  }

  span {
    font-size: 20px;
    font-weight: bold;
  }

  ul {
    margin-top: 30px;
    list-style-type: none;
    width: 464px;
    box-sizing: content-box;

    padding-left: ${({ circlePosition }) =>
      circlePosition === 'right' ? '0px' : '30px'};
  }

  li {
    line-height: 16px;
    letter-spacing: 0.32px;
    line-height: 26px;
    margin-top: 40px;
  }

  h5 {
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.36px;
    line-height: 26px;
    color: ${theme.colors.trialBlue};
    margin-bottom: 14px;
  }

  div {
    padding-left: ${({ circlePosition }) =>
      circlePosition === 'left' ? '30px' : '0'};
  }

  @media screen and (max-width: 1047px) {
    left: 0;
    margin-top: 50px;
    text-align: left;

    &:first-child {
      margin-top: 0;
    }

    h4 {
      display: inline-block;
      font-size: 30px;
      padding-bottom: 11px;
      padding-left: 0;

      &:after {
        display: none;
      }
    }

    ul {
      padding-left: 0;
      width: 100%;
      box-sizing: border-box;
      text-align: left;
      margin-top: 32px;
    }

    li {
      font-size: 15px;
      line-height: 25px;
      margin-top: 32px;

      br {
        display: none;
      }
    }

    h5 {
      margin-bottom: 12px;
    }

    div {
      padding-left: 0;
    }
  }
`

const PeriodImageWrapper = styled.div<{ justifyContent?: string }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ justifyContent }) => justifyContent};
  gap: 30px;
  margin-right: 40px;

  & + ul {
    margin-top: 33px;
  }

  img {
    max-width: 450px;
    border-radius: 10px;
  }

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: center;
    margin-right: 0;

    img {
      max-width: 100%;
      height: auto;
    }
  }
`

const NoteDiv = styled.div`
  width: 1040px;
  margin: 64px auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 50px 0 0;
  }
`

const Note = styled.p`
  line-height: 30px;
  letter-spacing: 0.32px;
  font-weight: 400;

  @media screen and (max-width: 1047px) {
    padding: 0 20px;
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const CardLinksDiv = styled.div`
  width: 100%;
  background-color: ${theme.colors.backgroundBlue};

  @media screen and (max-width: 1047px) {
    display: none;
  }
`
